import { Injectable, signal, effect } from '@angular/core';
import { CeRequest } from '../models/ce-request.model';

type DetailsRoute = '/new-project' | '/existing-project' | null;

const DRAFT_FORM_STORAGE_KEY = 'ce-request-draft-form';
const FLOW_PROGRESS_STORAGE_KEY = 'ce-request-flow-progress';

@Injectable({ providedIn: 'root' })
export class CeRequestStateService {
  readonly requests = signal<CeRequest[]>([]);
  
  readonly flowProgress = signal<{ detailsRoute: DetailsRoute; questionnaireVisited: boolean; responseVisited: boolean }>(
    this.loadFlowProgress()
  );

  readonly draftForm = signal(this.loadDraftForm());

  constructor() {
    // Auto-save draftForm to localStorage whenever it changes
    effect(() => {
      const formData = this.draftForm();
      localStorage.setItem(DRAFT_FORM_STORAGE_KEY, JSON.stringify(this.getPersistedDraftForm(formData)));
    });

    // Auto-save flowProgress to localStorage whenever it changes
    effect(() => {
      const progressData = this.flowProgress();
      localStorage.setItem(FLOW_PROGRESS_STORAGE_KEY, JSON.stringify(progressData));
    });
  }

  private getDefaultDraftForm() {
    return {
      estimateYear: '',
      sequenceNumber: '',
      projectTeam: '',
      afeNumber: '',
      isNewProject: true,
      estimateTitle: '',
      sequenceTitle: '',
      projectNumber: '',
      projectCode: '',
      estimateRequestType: '',
      projectManagementType: '',
      cpdepPhase: '',
      projectTitle: '',
      projectNumberFree: '',
      projectCodeFree: '',
      designProgress: '',
      anticipatedCompletionDate: '',
      area: '',
      unit: '',
      preliminaryCostDeadline: '',
      ibp: '',
      taYear: '',
    };
  }

  private getDefaultFlowProgress() {
    return {
      detailsRoute: null as DetailsRoute,
      questionnaireVisited: false,
      responseVisited: false,
    };
  }

  private loadDraftForm() {
    if (typeof localStorage === 'undefined') {
      return this.getDefaultDraftForm();
    }
    try {
      const defaults = this.getDefaultDraftForm();
      const saved = localStorage.getItem(DRAFT_FORM_STORAGE_KEY);
      if (!saved) {
        return defaults;
      }

      const parsed = JSON.parse(saved) as Partial<ReturnType<CeRequestStateService['getDefaultDraftForm']>>;
      return {
        ...defaults,
        ...parsed,
        // This field must be resolved from API for existing projects.
        isNewProject: defaults.isNewProject,
      };
    } catch (error) {
      console.error(error);
      return this.getDefaultDraftForm();
    }
  }

  private getPersistedDraftForm(formData: ReturnType<CeRequestStateService['getDefaultDraftForm']>) {
    const { isNewProject: _isNewProject, ...persisted } = formData;
    return persisted;
  }

  private loadFlowProgress() {
    if (typeof localStorage === 'undefined') {
      return this.getDefaultFlowProgress();
    }
    try {
      const saved = localStorage.getItem(FLOW_PROGRESS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : this.getDefaultFlowProgress();
    } catch (error) {
      console.error(error);
      return this.getDefaultFlowProgress();
    }
  }

  addRequest(): CeRequest {
    const form = this.draftForm();
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    const lastNum = this.requests().length + 1;
    const newRequest: CeRequest = {
      estimateNumber: `17-022-02-00-A-${String.fromCharCode(64 + lastNum)}`,
      projectTitle: form.projectTitle || 'New Project',
      requestDate: dateStr,
      status: 'Assigned CE',
    };

    this.requests.update(list => [newRequest, ...list]);
    return newRequest;
  }

  setDetailsRoute(route: '/new-project' | '/existing-project'): void {
    this.flowProgress.update(p => ({ ...p, detailsRoute: route }));
  }

  markQuestionnaireVisited(): void {
    this.flowProgress.update(p => ({ ...p, questionnaireVisited: true }));
  }

  markResponseVisited(): void {
    this.flowProgress.update(p => ({ ...p, responseVisited: true }));
  }

  resetDraft(): void {
    this.draftForm.set(this.getDefaultDraftForm());
    this.flowProgress.set(this.getDefaultFlowProgress());
    localStorage.removeItem(DRAFT_FORM_STORAGE_KEY);
    localStorage.removeItem(FLOW_PROGRESS_STORAGE_KEY);
  }
}

